import React, { useState, useRef } from "react";
import {
  Box,
  Heading,
  Text,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Button,
  VStack,
  useToast,
  Flex,
} from "@chakra-ui/react";
import { motion } from "framer-motion";
import emailjs from "emailjs-com";

// Example hero background
import heroBg from "../assets/contactus.jpg";
import heroBg2 from "../assets/contactus2.jpg";
const MotionBox = motion(Box);

export default function Contact() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const toast = useToast();
  const formRef = useRef();

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const validateForm = () => {
    const { email, phone } = formData;

    // Check if at least one of email or phone is provided
    if (!email.trim() && !phone.trim()) {
      toast({
        title: "Validation Error",
        description: "Either email or phone number is required.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
      return false;
    }

    // Validate phone number if entered
    if (phone && !/^\d{10}$/.test(phone)) {
      toast({
        title: "Validation Error",
        description: "Phone number must be exactly 10 digits.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
      return false;
    }

    return true;
  };

  const sendEmail = (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setLoading(true);

    emailjs
      .send(
        "service_vojlkcu",
        "template_wokon7j",
        {
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          company: formData.company,
          message: formData.message,
        },
        "zk53tof_9lJrHw1oM"
      )
      .then(() => {
        const email = formData.email?.trim();
        if (email) {
          return emailjs.send(
            "service_vojlkcu",
            "template_bqo83tm",
            {
              name: formData.name,
              email: email,
              phone: formData.phone,
              company: formData.company,
              to_email: email,
            },
            "zk53tof_9lJrHw1oM"
          );
        }
        return Promise.resolve();
      })
      .then(() => {
        setLoading(false);
        toast({
          title: "Message Sent",
          description: "Thank you for contacting us!",
          status: "success",
          duration: 4000,
          isClosable: true,
        });
        setFormData({
          name: "",
          email: "",
          phone: "",
          company: "",
          message: "",
        });
      })
      .catch((err) => {
        console.error("Email send error:", err);
        setLoading(false);
        toast({
          title: "Error",
          description: "Failed to send message. Please try again later.",
          status: "error",
          duration: 4000,
          isClosable: true,
        });
      });
  };

  return (
    <Box>
      <Box bg="#F5FAF6" color="#305D24">
        {/* Hero Section with background image */}
        <MotionBox
          position="relative"
          bgImage={`url(${heroBg})`}
          bgSize="cover"
          bgPosition="center"
          shadow="xl"
          mb={10}
          py={{ base: 20, md: 32 }}
          textAlign="center"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          {/* Dark overlay */}
          <Box
            position="absolute"
            top={0}
            left={0}
            width="100%"
            height="100%"
            bg="rgba(0,0,0,0.5)"
          />
          <Box
            position="relative"
            zIndex={1}
            px={{ base: 4, md: 20 }}
            pt={{ base: 28, md: 0 }}
          >
            <Heading fontSize={{ base: "3xl", md: "5xl" }} color="white">
              Contact Us
            </Heading>
            <Text
              fontWeight="bold"
              fontSize={{ base: "md", md: "lg" }}
              mt={4}
              maxW="3xl"
              mx="auto"
              color="white"
            >
              We're making a sustainable future possible — one solar solution at
              a time. Partner with HoriSun to bring clean energy to life — reach
              out today.
            </Text>
          </Box>
        </MotionBox>

        {/* Form + Image Section */}
        <MotionBox
          py={10}
          px={{ base: 4, md: 16 }}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <Flex
            maxW="1200px"
            mx="auto"
            direction={{ base: "column", md: "row" }}
            align="stretch"
            gap={10}
          >
            {/* Left: Form */}
            <Box
              flex={{ base: "1", md: "0.4" }}
              bg="white"
              p={8}
              rounded="xl"
              shadow="2xl"
            >
              <Heading fontSize="2xl" mb={6} textAlign="center" color="#065F46">
                Talk to Our Team
              </Heading>
              <Box as="form" ref={formRef} onSubmit={sendEmail}>
                <VStack spacing={4}>
                  <FormControl id="name" isRequired>
                    <FormLabel>Your Name</FormLabel>
                    <Input
                      type="text"
                      name="name"
                      placeholder="John Doe"
                      value={formData.name}
                      onChange={handleChange}
                    />
                  </FormControl>

                  <FormControl id="company">
                    <FormLabel>Company Name</FormLabel>
                    <Input
                      type="text"
                      name="company"
                      placeholder="Company Inc."
                      value={formData.company}
                      onChange={handleChange}
                    />
                  </FormControl>

                  <FormControl id="email">
                    <FormLabel>Email Address</FormLabel>
                    <Input
                      type="email"
                      name="email"
                      placeholder="john@example.com"
                      value={formData.email}
                      onChange={handleChange}
                    />
                  </FormControl>

                  <FormControl id="phone">
                    <FormLabel>Phone Number</FormLabel>
                    <Input
                      type="tel"
                      name="phone"
                      placeholder="1234567890"
                      value={formData.phone}
                      onChange={handleChange}
                    />
                  </FormControl>

                  <FormControl id="message" isRequired>
                    <FormLabel>Message</FormLabel>
                    <Textarea
                      name="message"
                      placeholder="Write your message here..."
                      value={formData.message}
                      onChange={handleChange}
                      rows={6}
                    />
                  </FormControl>

                  <Button
                    type="submit"
                    bg="#FBBF24"
                    color="#065F46"
                    w="full"
                    _hover={{ bg: "#F59E0B" }}
                    isLoading={loading}
                  >
                    Send Message
                  </Button>
                </VStack>
              </Box>
            </Box>

            {/* Right: Big Image */}
            {/* Right: Split Vertical */}
            <Flex flex={{ base: "1", md: "0.6" }} direction="column" gap={2}>
              {/* Top: Paragraph Box */}
              <Box
                bg="rgba(245, 250, 246, 0.9)"
                p={{ base: 6, md: 12 }}
                rounded="xl"
                shadow="2xl"
                flex="1"
              >
                <Heading
                  fontSize={{ base: "xl", md: "3xl" }}
                  mb={4}
                  color="#065F46"
                >
                  Have a Question? Contact Us
                </Heading>
                <Text
                  fontSize={{ base: "sm", md: "md" }}
                  mb={4}
                  color="#305D24"
                  lineHeight="taller"
                >
                  You will receive a response from our team at the earliest
                  opportunity.
                </Text>
                <Text
                  fontSize={{ base: "sm", md: "md" }}
                  color="#305D24"
                  lineHeight="taller"
                >
                  Whether you are considering the installation of solar panels
                  for a residential or commercial property, or undertaking a
                  large-scale solar development, we offer comprehensive,
                  end-to-end services — from initial design through to final
                  commissioning. Our team will work closely with you throughout
                  each phase of the project to ensure timely delivery, cost
                  efficiency, and full alignment with your specifications.
                </Text>
              </Box>

              {/* Bottom: Existing Image */}
              <Box flex="1" rounded="xl" overflow="hidden" shadow="2xl">
                <img
                  src={heroBg2} // replace with your actual image
                  alt="Contact Illustration"
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              </Box>
            </Flex>
          </Flex>
        </MotionBox>

        {/* Google Map */}
        <MotionBox
          mt={16}
          maxW="100%"
          mx="auto"
          borderRadius="2xl"
          overflow="hidden"
          shadow="xl"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <iframe
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d343.03202084450964!2d76.6934844!3d30.7115125!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x390fef007a1d6dcf%3A0xeeb435bcbbba5ae4!2sVictory%20tower!5e0!3m2!1sen!2sin!4v1695978930000!5m2!1sen!2sin"
            width="100%"
            height="450"
            style={{ border: 0 }}
            allowFullScreen=""
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            title="HoriSun Location"
          ></iframe>
        </MotionBox>
      </Box>

    </Box>
  );
}
